import { ArrowRight, Check, FileSpreadsheet } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';
import type { SlideProps } from '@/lib/whats-new/edition-types';
import { formatCount } from '@/lib/whats-new/formatting';
import { useSteps } from '@/components/whats-new/editions/estreia/use-steps';
import { csvVM } from '@/components/whats-new/editions/estreia/view-models';
import { CountUp } from '@/components/whats-new/slides/count-up';
import { Eyebrow } from '@/components/whats-new/slides/eyebrow';
import { GlassCard } from '@/components/whats-new/slides/glass-card';
import { Rise } from '@/components/whats-new/slides/rise';
import { SampleBadge } from '@/components/whats-new/slides/sample-badge';
import {
  SlideLayout,
  SlideLead,
  SlideTitle,
} from '@/components/whats-new/slides/slide-layout';
import { WordReveal } from '@/components/whats-new/slides/word-reveal';

/** The columns are recognized after the card comes in. */
export const CSV_MAPPED_MS = 900;
/** Then the rows turn "Agendada" one by one, 320 ms apart (NEWS-30). */
export const CSV_FIRST_ROW_MS = 2300;
export const CSV_ROW_STAGGER_MS = 320;
/** And the footer counts and "Nada enviado…" show up. */
export const CSV_DONE_MS = 3600;
/** Each mapping lights up 140 ms after the previous one. */
const MAPPING_STAGGER_MS = 140;

const ROWS = 4;
const STEPS: readonly number[] = [
  CSV_MAPPED_MS,
  ...Array.from(
    { length: ROWS },
    (_, i) => CSV_FIRST_ROW_MS + i * CSV_ROW_STAGGER_MS
  ),
  CSV_DONE_MS,
];

// Arbitrary properties: Tailwind's arbitrary duration and easing classes
// clash with tailwindcss-animate's and generate nothing.
const FADE =
  'transition-[opacity,transform,background-color,box-shadow] [transition-duration:450ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]';

/**
 * 3 · CSV (NEWS-30): a spreadsheet read by the import. Its 4 columns light
 * up as recognized, 4 sample rows go from "Revisar" to "Agendada" and the
 * footer counts what got scheduled and what stays a draft. Sample rows of
 * the module, marked "Exemplo".
 */
export function CsvSlide(_props: SlideProps) {
  const { LL, locale } = useI18nContext();
  const vm = csvVM();
  const step = useSteps(STEPS);
  const mapped = step >= 1;
  const scheduledRows = Math.min(ROWS, Math.max(0, step - 1));
  const done = step >= STEPS.length;
  const count = (value: number) => formatCount(Math.round(value), locale);

  return (
    <SlideLayout
      copy={
        <>
          <Rise index={0}>
            <Eyebrow lead={LL.whatsNew.editions.estreia.csv.eyebrow()} />
          </Rise>
          <SlideTitle>
            <WordReveal text={LL.whatsNew.editions.estreia.csv.title()} />
          </SlideTitle>
          <Rise index={2}>
            <SlideLead>{LL.whatsNew.editions.estreia.csv.lead()}</SlideLead>
          </Rise>
        </>
      }
      visual={
        <Rise index={1}>
          <GlassCard className="p-[18px] max-md:p-3.5">
            <div className="flex items-center gap-3 border-b border-white/[.12] pb-3.5">
              <span className="grid size-9 flex-none place-items-center rounded-[9px] bg-[rgba(111,160,255,0.18)] text-[#CFE0FF]">
                <FileSpreadsheet
                  aria-hidden="true"
                  className="size-[18px]"
                  strokeWidth={1.6}
                />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">
                  {LL.whatsNew.editions.estreia.csv.fileName()}
                </p>
                <p className="text-xs text-white/[.64]">
                  {LL.whatsNew.editions.estreia.csv.fileMeta({
                    rows: vm.scheduled + vm.drafts,
                  })}
                </p>
              </div>
              <span className="ml-auto flex flex-none items-center gap-2.5">
                {vm.sample && <SampleBadge />}
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#CFE0FF]">
                  <Check aria-hidden="true" className="size-[13px]" />
                  {LL.whatsNew.editions.estreia.csv.fileRead()}
                </span>
              </span>
            </div>

            <ul className="grid grid-cols-2 gap-1.5 py-3.5">
              {vm.mappings.map((mapping, k) => (
                <li
                  key={k}
                  data-testid="csv-mapping"
                  data-mapped={mapped || undefined}
                  className={cn(
                    'flex min-w-0 items-center gap-[7px] rounded-lg px-[9px] py-[7px] text-xs',
                    FADE,
                    mapped
                      ? 'bg-[rgba(111,160,255,0.14)] shadow-[inset_0_0_0_1px_rgba(111,160,255,0.4)]'
                      : 'bg-white/[.05] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)]'
                  )}
                  style={{ transitionDelay: `${k * MAPPING_STAGGER_MS}ms` }}
                >
                  <code className="font-mono text-[11.5px] font-medium text-white/[.86]">
                    {mapping.column(LL)}
                  </code>
                  <ArrowRight
                    aria-hidden="true"
                    className="size-3 flex-none text-white/[.64]"
                  />
                  <b
                    className={cn(
                      'truncate font-semibold',
                      FADE,
                      mapped ? 'opacity-100' : 'opacity-[.35]'
                    )}
                    style={{
                      transitionDelay: `${k * MAPPING_STAGGER_MS + 100}ms`,
                    }}
                  >
                    {mapping.field(LL)}
                  </b>
                </li>
              ))}
            </ul>

            <ul className="flex flex-col gap-1.5 pb-3.5">
              {vm.rows.map((row, i) => {
                const scheduled = i < scheduledRows;
                return (
                  <li
                    key={i}
                    data-testid="csv-row"
                    data-status={scheduled ? 'scheduled' : 'review'}
                    className="grid grid-cols-[minmax(0,1fr)_auto_86px] items-center gap-2.5 rounded-lg bg-white/[.04] px-2.5 py-2 text-[12.5px] max-md:grid-cols-[minmax(0,1fr)_auto_76px] max-md:gap-2"
                  >
                    <span className="truncate font-semibold">
                      {row.name(LL)}
                    </span>
                    <span className="whitespace-nowrap tabular-nums text-white/[.86]">
                      {row.when(LL)}
                    </span>
                    <span className="relative grid h-[22px]">
                      <span
                        aria-hidden={scheduled || undefined}
                        className={cn(
                          'absolute inset-0 grid place-items-center rounded-full bg-white/[.08] text-[11px] font-semibold text-white/[.86]',
                          FADE,
                          scheduled && 'opacity-0'
                        )}
                      >
                        {LL.whatsNew.editions.estreia.csv.statusReview()}
                      </span>
                      <span
                        aria-hidden={!scheduled || undefined}
                        className={cn(
                          'absolute inset-0 grid place-items-center rounded-full bg-white text-[11px] font-semibold text-[#15191E]',
                          FADE,
                          scheduled ? 'opacity-100' : 'scale-[.7] opacity-0'
                        )}
                      >
                        {LL.whatsNew.editions.estreia.csv.statusScheduled()}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="flex flex-wrap items-end gap-x-[22px] gap-y-2 border-t border-white/[.12] pt-3.5">
              <div data-testid="csv-scheduled" className="flex flex-col">
                <span className="text-xs text-white/[.86]">
                  {LL.whatsNew.editions.estreia.csv.scheduled()}
                </span>
                <b className="text-[26px] font-semibold tracking-[-0.02em]">
                  <CountUp
                    value={vm.scheduled}
                    format={count}
                    delayMs={CSV_DONE_MS}
                  />
                </b>
              </div>
              <div data-testid="csv-drafts" className="flex flex-col">
                <span className="text-xs text-white/[.86]">
                  {LL.whatsNew.editions.estreia.csv.drafts()}
                </span>
                <b className="text-[26px] font-semibold tracking-[-0.02em]">
                  <CountUp
                    value={vm.drafts}
                    format={count}
                    delayMs={CSV_DONE_MS}
                  />
                </b>
              </div>
              <span
                data-testid="csv-nothing-sent"
                data-shown={done || undefined}
                className={cn(
                  'ml-auto inline-flex h-[22px] items-center gap-1 rounded-full bg-[rgba(111,160,255,0.18)] px-2 text-xs font-semibold text-[#CFE0FF]',
                  'transition-opacity [transition-duration:600ms]',
                  done ? 'opacity-100' : 'opacity-0'
                )}
              >
                <Check aria-hidden="true" className="size-[13px]" />
                {LL.whatsNew.editions.estreia.csv.nothingSent()}
              </span>
            </div>
          </GlassCard>
        </Rise>
      }
    />
  );
}
